import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const inputCls =
    "w-full bg-transparent border-b border-border rounded-none px-0 py-3 text-foreground placeholder:text-muted-foreground/70 focus:outline-none focus:border-primary transition-colors duration-300 font-sans";

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back.");
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Invalid email or password");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="page-admin-login"
      className="min-h-screen bg-background text-foreground flex items-center justify-center px-6 py-24"
    >
      <div className="w-full max-w-md fade-up">
        <div className="flex items-center gap-3 text-primary">
          <Lock size={16} strokeWidth={1.5} />
          <span className="overline">Admin · Nelum Dining</span>
        </div>
        <h1 className="mt-6 serif text-5xl md:text-6xl font-light leading-[0.95] tracking-tighter text-foreground">
          Sign in.
        </h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Restricted access. Contact management for credentials.
        </p>

        <form
          data-testid="admin-login-form"
          onSubmit={handleSubmit}
          className="mt-12 space-y-10"
        >
          <div>
            <label className="overline block mb-4">Email</label>
            <input
              data-testid="admin-login-email"
              className={inputCls}
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="admin@nelumdining.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="overline block mb-4">Password</label>
            <input
              data-testid="admin-login-password"
              className={inputCls}
              type="password"
              autoComplete="current-password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>
          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-3 px-8 py-4 bg-primary text-primary-foreground text-[0.72rem] uppercase tracking-[0.24em] font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div className="mt-16 pt-8 border-t border-border">
          <a
            href="/"
            data-testid="admin-login-back-home"
            className="text-[0.72rem] uppercase tracking-[0.22em] text-muted-foreground hover:text-primary transition-colors"
          >
            ← Back to site
          </a>
        </div>
      </div>
    </div>
  );
}
